import { CalculatorIcon, PencilIcon, TrashIcon } from '@heroicons/react/outline';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Button, Checkbox, IconButton, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowId, GridRowParams, GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@mui/x-data-grid-pro';
import { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { IContactHeaderCreditScore, ICreditScore } from '@modules/ContactManager/components/Header/interfaces';
import { NContact } from '@modules/ContactManager/components/Tabs/components/Contact/validation';
import { DealcalculatorErrorModal } from '@modules/ContactManager/components/Tabs/components/Leads/components/DealCalculatorErrorModal/DealCalculatorErrorModal';

// import { UnIdentifiedDealsModal } from '@modules/accounting/CheckerMenu/DealsQueue/UnIdentifiedDealsModal';
import { queryParams } from '@shared/commonFunctions';
import { dataGridCol, gridCurrencyCell, gridPercentageCell } from '@shared/components/grid/common';
import { GridCell } from '@shared/components/grid/GridCell';
import { gridRowActionsIconProps } from '@shared/components/grid/GridColActions';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { SERVICES } from '@shared/constants/apiConfig';
import { useTabs } from '@shared/hooks/useTabs';
import { DealsPhoenixGrid } from '@styles/ContactManager/DealStyles';
import { GridWrapper } from '@styles/GridStyles';
import { GridColActionsWrapper } from '@styles/PhoenixGrid/GridStyles';
import { ExpandedDealsWrapper, HorizontalScrollStyle } from '@styles/StyledDiv';

import { IDealCalculatorRequest } from '../../../shared/DealCalculator/interfaces';
import { getAssetValue } from '../../Assets/utils';
import { ECo, IDealsTableProps, IDealSummary } from '../interfaces';

interface IDealRow extends IDealSummary {
  deals: IDealSummary[];
}

const getDealsMapped = (data: IDealSummary[]) => {
  const leadIdMap: { [key: string]: IDealSummary[] } = {};

  data?.forEach((deal) => {
    if (!leadIdMap[deal.leadId]) {
      leadIdMap[deal.leadId] = [];
    }

    leadIdMap[deal.leadId].push(deal);
  });

  return Object.keys(leadIdMap).map((leadId) => {
    const [picked, ...rest] = leadIdMap[leadId].sort((a, b) => +b.isPicked - +a.isPicked);
    return {
      ...picked,
      deals: rest,
    };
  });
};

const ExpandedDeals = ({
  row,
  dealId,
  contactAddress,
  onCalculate,
  onSelect,
  handleDealToggle,
  deleteDeal,
  selectDealId
}: {
  row: IDealRow;
  dealId: string;
  selectDealId: (dealId: string) => void;
  contactAddress?: NContact.Address;
  onCalculate: (deal: IDealCalculatorRequest) => void;
  onSelect?(row: IDealSummary): void;
  handleDealToggle(dealId: string): void;
  deleteDeal: ((deal: string) => void) | undefined;
}) => (
  <ExpandedDealsWrapper>
    <DealsTable
      expandable={false}
      data={row.deals}
      selectDealId={selectDealId}
      dealId={dealId}
      contactAddress={contactAddress}
      handleDealToggle={handleDealToggle}
      deleteDeal={deleteDeal}
      onSelect={onSelect}
      onCalculate={onCalculate}
    />
  </ExpandedDealsWrapper>
);

export const DealsTable = ({
  data,
  error,
  validatingDeals = false,
  onCalculate,
  expandable = true,
  dealId,
  contactAddress,
  onSelect,
  handleDealToggle,
  deleteDeal,
  selectDealId,
  setShowDealBuilder,
}: IDealsTableProps) => {
  const { updateCurrentTab } = useTabs();
  const param = queryParams();

  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const isExpanded = Boolean(expandedRows.length);
  const rows: Array<IDealSummary | IDealRow> | undefined = expandable ? getDealsMapped(data as IDealSummary[]) : data || [];
  
  const { data: headerData } = useSWR<IContactHeaderCreditScore>(`/${SERVICES.CONTACT}/contacts/${queryParams()?.id}/header`);
  const [deletingDeal, setDeletingDeal] = useState<string>('');
  const [isOpenDealCalculatorErrorModal, setIsOpenDealCalculatorErrorModal] = useState(0);
  const [ids, setIds] = useState({
    dealId: '',
    leadId: '',
  });

  const isLoading = useMemo(() => (!data && !error) || validatingDeals, [data, error, validatingDeals]);

  useEffect(() => {
    const pickedDeal = data?.find(item => item.dealId === param?.dealId);
    const dealsWithSameLeadId = data?.filter(item => item?.leadId === pickedDeal?.leadId);
    const activeDeal = dealsWithSameLeadId?.find(item => item?.isPicked);
    
    if (!pickedDeal?.isPicked) {
        const dealIdToExpand = activeDeal?.dealId ? [activeDeal?.dealId] : [];
        setExpandedRows(dealIdToExpand);
    }
}, [data, param?.dealId, param?.leadId]);

  useEffect(
    () => {
      if (headerData) {
        updateCurrentTab({ title: `${headerData.firstName} ${headerData.lastName}` });
      }
    },
    [headerData]
  );

  useEffect(() => {
    if (rows?.length) {
      if (!dealId) {
        const pickedDeal = rows?.find((deal) => deal.isPicked);
        const pickedDealForCurrentLead = rows?.find((deal) => deal.isPicked && deal.leadId == param?.leadId);
        onSelect?.(pickedDealForCurrentLead ?? pickedDeal ?? rows[0]);
      } else {
        const selectedDeal = rows?.find((deal) => deal.dealId === dealId);
        selectedDeal && onSelect?.(selectedDeal);
      }
      setShowDealBuilder && setShowDealBuilder(true);
    }
  }, [rows?.length, dealId]);

  const handleShowAllButton = useCallback(() => {
    if (rows) {
      setExpandedRows(rows.map((item) => item.dealId));
    }
  }, [rows]);

  const handleCloseAll = useCallback(() => {
    setExpandedRows([]);
  }, []);

  const columns: GridColDef<IDealSummary>[] = [
    dataGridCol('Show all', {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 80,
      headerName: isExpanded ? 'Close All' : 'Show all',
      renderHeader: () => (
        <Button onClick={isExpanded ? handleCloseAll : handleShowAllButton}>{`${isExpanded ? 'Close all' : 'Show all'}`}</Button>
      ),
      renderCell: (params: GridRenderCellParams) =>
        params.row?.deals?.length > 0
          ?
          <>{params.row?.deals?.length + 1}{params.value ? <ExpandLess /> : <ExpandMore />}</>
          :
          <>{params.row?.deals?.length + 1}{params.value}</>
    }),
    dataGridCol('isPicked', {
      headerName: 'Active',
      minWidth: 40,
      renderCell: ({ value, row }: GridRenderCellParams) =>
        row.isPicked ? (
          <Typography sx={{ marginLeft: 2 }}>Yes</Typography>
        ) : (
          <Checkbox
            checked={!!value}
            value={value || ''}
            disabled={row.isPicked}
            onClick={() => {
              handleDealToggle && handleDealToggle(row.dealId);
            }}
          />
        ),
    }),
    dataGridCol('dealNumericId', {
      headerName: 'Deal Id',
      minWidth: 100,
      flex: 1,
    }),
    dataGridCol('currentWorkflowStatus', {
      headerName: 'Status',
      minWidth: 120,
      flex: 1,
    }),
    dataGridCol('lenderName', {
      headerName: 'Lender',
      minWidth: 120,
      flex: 2,
    }),
    dataGridCol('assetTag', {
      headerName: 'Asset',
      minWidth: 200,
      flex: 4,
      valueGetter: (params) => getAssetValue(params?.value)
    }),
    dataGridCol('rate', {
      ...gridPercentageCell,
      minWidth: 40,
      renderEditCell: (params) => (
        <GridColNumber
          params={params}
          suffix="%"
          decimalScale={2}
        />
      ),
    }),
    dataGridCol('term', { minWidth: 40 }),
    dataGridCol('monthlyPayment', {
      ...gridCurrencyCell,
      headerName: 'Payment',
      minWidth: 50,
    }),
    dataGridCol('co', {
      headerName: 'CO',
      minWidth: 30,
      renderCell: ({ value }) => <GridCell text={ECo[value]} />
    }),
    dataGridCol('actions', {
      minWidth: 130,
      renderCell: ({ row }) => (
        <GridColActionsWrapper margin="-5px">
          <IconButton
            data-action="edit-row"
            onClick={() => {
              onSelect?.(row.dealId);
            }}
          >
            <PencilIcon {...gridRowActionsIconProps} />
          </IconButton>

          <IconButton
            data-action="edit-row"
            onClick={() => calculate(row)}
          >
            <CalculatorIcon {...gridRowActionsIconProps} />
          </IconButton>

          {deleteDeal && !row.isPicked && row.currentWorkflowStatus.includes('DRAFT') && (
            <IconButton
              data-action="edit-row"
              onClick={() => setDeletingDeal(row.dealId)}
            >
              <TrashIcon {...gridRowActionsIconProps} />
            </IconButton>
          )}
        </GridColActionsWrapper>
      ),
    }),
  ];

  const dealCalcHandler = useCallback(
    (score: number) => {
      onCalculate({
        dealId: ids?.dealId,
        estimatedScore: score,
        leadId: ids?.leadId,
        isEstimatedScore: true,
        isDealEdit: true,
      });
      setIsOpenDealCalculatorErrorModal(0);
    },
    [ids]
  );

  const calculate = (row: GridValidRowModel) => {
    setIds({
      dealId: row?.dealId,
      leadId: row?.leadId,
    });
    const creditScore = headerData?.creditScore.find((v: ICreditScore) => v?.bureauType === 'Transunion' && v?.scoreType === 'Tu')?.fico;

    if (!creditScore) {
      setIsOpenDealCalculatorErrorModal(prevState => prevState + 1);
    } else {
      const finalScore = creditScore.includes('/') ? creditScore.split('/')[0] : creditScore;
      onCalculate({
        dealId: row?.dealId,
        estimatedScore: Number(finalScore),
        leadId: row?.leadId,
        isEstimatedScore: false,
        isDealEdit: true,
      });
      setDeletingDeal('');
    }
  };

  const handleDeleteClick = useCallback(() => {
    deleteDeal?.(deletingDeal);
    setDeletingDeal('');
  }, [deletingDeal]);

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams) =>
      row.deals?.length ? (
        <ExpandedDeals
          row={row}
          dealId={dealId}
          selectDealId={selectDealId}
          contactAddress={contactAddress}
          handleDealToggle={handleDealToggle}
          deleteDeal={deleteDeal}
          onSelect={onSelect}
          onCalculate={onCalculate}
        />
      ) : null,
    [dealId, rows]
  );

  return (
    <GridWrapper>
      {deletingDeal && (
        <AlertModal
          title={'Delete deal'}
          subtitle={'Are you sure you want to delete this deal?'}
          open={!!deletingDeal}
          onConfirm={handleDeleteClick}
          onClose={() => setDeletingDeal('')}
        />
      )}

      {!!isOpenDealCalculatorErrorModal && (
        <DealcalculatorErrorModal
          DealcalcErrModalPostiton={isOpenDealCalculatorErrorModal}
          dealCalcHandler={dealCalcHandler}
          handleClose={() => setIsOpenDealCalculatorErrorModal(0)}
        />
      )}

      <HorizontalScrollStyle>
        <DealsPhoenixGrid
          canNotCopy
          hideFooter
          autoHeight
          disableSelectionOnClick
          name="Deal"
          detailPanelExpandedRowIds={expandedRows}
          columns={columns}
          pageSize={rows.length}
          loading={isLoading}
          rows={isLoading ? [] : rows}
          getRowId={(row) => row.dealId}
          selectionModel={dealId ? [dealId] : []}
          getDetailPanelContent={expandable ? getDetailPanelContent : undefined}
          getDetailPanelHeight={() => 'auto'}
          onDetailPanelExpandedRowIdsChange={(value: GridRowId[]) => setExpandedRows(value as string[])}
          onRowClick={(params: GridRowParams) => onSelect?.(params.row)}
        />
      </HorizontalScrollStyle>
    </GridWrapper>
  );
};
